import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
import { Client } from '../../types';
import { generateClientKey } from './keys';
import { createRevenuesLedger, getClientLedger } from './revenuesLedgers';

// Clients Collection
export const addClient = async (client: Omit<Client, 'id' | 'createdAt'>): Promise<string> => {
  try {
    // Create custom key: name-uniqueId
    const customKey = generateClientKey(client.name, client.uniqueId);
    
    // Check if client already exists
    const existingClient = await getClientById(customKey);
    if (existingClient) {
      throw new Error(`Client with name ${client.name} and uniqueId ${client.uniqueId} already exists`);
    }
    
    const docRef = doc(db, 'clients', customKey);
    await setDoc(docRef, {
      ...client,
      createdAt: serverTimestamp()
    });
    
    // Automatically create revenues ledger for the client with opening balance
    await createRevenuesLedger(customKey, client.name, client.openingBalance || 0);
    
    return customKey;
  } catch (error) {
    throw new Error(`Error adding client: ${error}`);
  }
};

export const getClients = async (): Promise<Client[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'clients'));
    const clients = querySnapshot.docs.map(doc => ({
      id: doc.id, // This will now be the custom key (name-uniqueId)
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Client[];

    // Calculate outstanding using computed ledger (includes opening balance and payments)
    const withOutstanding = await Promise.all(
      clients.map(async (client) => {
        try {
          const ledger = await getClientLedger(client.id);
          return { ...client, outstanding: ledger.currentBalance } as Client;
        } catch (error) {
          return client;
        }
      })
    );

    return withOutstanding;
  } catch (error) {
    throw new Error(`Error getting clients: ${error}`);
  }
};

export const getClientById = async (clientId: string): Promise<Client | null> => {
  try {
    const docRef = doc(db, 'clients', clientId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id, // This will be the custom key
        ...data,
        createdAt: data.createdAt?.toDate() || new Date()
      } as Client;
    }
    return null;
  } catch (error) {
    throw new Error(`Error getting client: ${error}`);
  }
};

// Helper function to get client by name and uniqueId
export const getClientByNameAndUniqueId = async (name: string, uniqueId: string): Promise<Client | null> => {
  try {
    const customKey = generateClientKey(name, uniqueId);
    return await getClientById(customKey);
  } catch (error) {
    throw new Error(`Error getting client by name and uniqueId: ${error}`);
  }
};

// Delete client and all related data
export const deleteClient = async (clientId: string): Promise<void> => {
  try {
    // Delete the client document
    const clientRef = doc(db, 'clients', clientId);
    await deleteDoc(clientRef);
    
    // Delete all related invoices
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('clientId', '==', clientId)
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    const deleteInvoicePromises = invoicesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteInvoicePromises);
    
    // Delete all related ledger entries
    const ledgerQuery = query(
      collection(db, 'ledgerEntries'),
      where('clientId', '==', clientId)
    );
    const ledgerSnapshot = await getDocs(ledgerQuery);
    const deleteLedgerPromises = ledgerSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteLedgerPromises);
    
    // Delete the revenues ledger
    const revenuesLedgerQuery = query(
      collection(db, 'revenuesLedgers'),
      where('clientId', '==', clientId)
    );
    const revenuesLedgerSnapshot = await getDocs(revenuesLedgerQuery);
    const deleteRevenuesLedgerPromises = revenuesLedgerSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteRevenuesLedgerPromises);
  } catch (error) {
    throw new Error(`Error deleting client: ${error}`);
  }
};
